import { useState, useEffect } from 'react'
import { Icon } from '@iconify/react'
import { useNavigate } from 'react-router-dom'
import { useGlobalContext } from '../../../../utils/Context'
import PageLoader from '../../../../components/loader/PageLoader'
import { get } from '../../../../utils/Api'
import { getDebtSummary } from '../../../../utils/DebtBatchesApi'
import FormLoader from '../../../../components/loader/FormLoader'
import ChangePassword from './ChangePassword'
import PaymentModal from './PaymentModal'

function Home() {
  const {
    notify,
    profile,
    fetchUserProfile,
    fetchBusiness,
    businessLoading,
    business,
  } = useGlobalContext()
  const navigate = useNavigate()

  //<-------------- Business Details --------------->
  useEffect(() => {
    fetchBusiness()
    fetchUserProfile()
  }, [])

  //<-------------- Debt Summary --------------->
  const [debtSummary, setDebtSummary] = useState<any>(null)

  function fetchDebtSummary() {
    getDebtSummary()
      .then((result: any) => {
        if (result.status === 200) {
          setDebtSummary(result.data)
        } else {
          // Silently fail if debt batches not available
          console.log('Debt summary not available')
        }
      })
      .catch(() => {
        // Silently fail if debt batches API not available
      })
  }

  useEffect(() => {
    fetchDebtSummary()
  }, [])

  //<-------------- Bookings --------------->
  const [bookings, setBookings] = useState<any>({ data: [] })
  const [bookingsLoading, setBookingsLoading] = useState(true)

  function fetchBookings() {
    setBookingsLoading(true)
    get('business_admin/bookings?page=1&limit=10')
      .then((result: any) => {
        if (result.status === 200) {
          setBookings(result)
        } else {
          notify('error', result.message)
        }
        setBookingsLoading(false)
      })
      .catch(() => {
        setBookingsLoading(false)
      })
  }

  useEffect(() => {
    fetchBookings()
  }, [])

  //<-------------- Transactions --------------->
  const [transactions, setTransactions] = useState<any>({ transactions: [] })
  const [transactionsLoading, setTransactionsLoading] = useState(true)

  function fetchTransactions() {
    setTransactionsLoading(true)
    get('business_admin/transactions?page=1&limit=10')
      .then((result: any) => {
        if (result.status === 200) {
          setTransactions(result)
        } else {
          notify('error', result.message)
        }
        setTransactionsLoading(false)
      })
      .catch(() => {
        setTransactionsLoading(false)
      })
  }

  useEffect(() => {
    fetchTransactions()
  }, [])

  // Payment
  const [showPayment, setShowPayment] = useState(false)

  return businessLoading ? (
    <div className='home'>
      <PageLoader position='center' />
    </div>
  ) : (
    <div className='home'>
      {profile.first_time_login && <ChangePassword />}
      {showPayment && (
        <PaymentModal
          usage='debt'
          outstanding={business.outstanding}
          close={() => {
            fetchTransactions()
            setShowPayment(false)
          }}
        />
      )}

      {/* Debt Blocking Alert */}
      {debtSummary?.isBlocked && !debtSummary?.hasGrace && (
        <div
          style={{
            padding: '1rem',
            margin: '1rem 0',
            backgroundColor: '#f8d7da',
            border: '1px solid #f5c6cb',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Icon
            icon='mdi:alert-circle'
            style={{
              marginRight: '0.5rem',
              color: '#721c24',
              fontSize: '1.5rem',
            }}
          />
          <div>
            <strong>Booking Suspended:</strong> You have ₦
            {debtSummary?.overdueAmount?.toLocaleString()} in overdue debt.
            Please clear to continue booking.{' '}
            <a
              href='/developer/debt-batches'
              style={{ color: '#721c24', textDecoration: 'underline' }}
            >
              View Details
            </a>
          </div>
        </div>
      )}

      {/* Grace Period Alert */}
      {debtSummary?.hasGrace && (
        <div
          style={{
            padding: '1rem',
            margin: '1rem 0',
            backgroundColor: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Icon
            icon='mdi:information'
            style={{
              marginRight: '0.5rem',
              color: '#856404',
              fontSize: '1.5rem',
            }}
          />
          <div>
            <strong>Grace Period Active:</strong> Extended until{' '}
            {new Date(debtSummary.graceUntil).toLocaleString()}
          </div>
        </div>
      )}

      <div className='home-head flex justify-between flex-wrap'>
        <div className='left flex justify-between '>
          <div className='card'>
            <p>Outstanding</p>
            <h2>₦{business.outstanding.toLocaleString()}</h2>
            <button onClick={() => navigate('/developer/debt-batches')}>
              view
            </button>
          </div>
          <div className='card'>
            <p>Booking Count</p>
            <h2>{business.totalBookings.toLocaleString()}</h2>
          </div>
          <div className='card'>
            <p>Total Booking Value</p>
            <h2>₦{business.totalCharge.toLocaleString()}</h2>
          </div>
          <div className='card'>
            <p>Credit Limit</p>
            <h2>₦{business.creditLimit.toLocaleString()}</h2>
          </div>
        </div>
      </div>
      <div className='aza flex justify-between items-center flex-wrap'>
        <div className='card'>
          <h3>Wallet Balance</h3>
          <p>₦{business.balance.toLocaleString()}</p>
          <button onClick={() => navigate('/developer/wallet-trail')}>
            view
          </button>
        </div>
        <div className='card'>
          <h3>Account Name</h3>
          <p>{business.org_name}</p>
        </div>
        <div className='card'>
          <h3>Account Number</h3>
          <p>{business.account_number}</p>
        </div>
        <div className='card'>
          <h3>Bank Name</h3>
          <p>{business.bank}</p>
        </div>
      </div>
      <div className='home_tables'>
        <div className='left'>
          <div className='left_top'>
            <h2>Recent Bookings</h2>
          </div>
          {bookingsLoading ? (
            <FormLoader position='center' />
          ) : (
            <>
              {bookings.data.length > 0 ? (
                <>
                  <div className='mobile-table'>
                    {bookings.data.map((item: any, i: number) => (
                      <div
                        key={item.id}
                        className='card'
                        onClick={() =>
                          navigate(`/developer/booking/${item.id}`)
                        }
                      >
                        <div className='flex justify-between row'>
                          <div className='count flex items-center justify-center'>
                            {i + 1}
                          </div>
                          <p> {new Date(item.createdAt).toLocaleString()}</p>
                        </div>
                        <div className='row flex justify-between'>
                          <h4>Amount</h4>
                          <p>₦{item.charge.toLocaleString()}</p>
                        </div>
                        <div className='row flex justify-between'>
                          <h4>Shipment type</h4>
                          <p>{item.shipment_type}</p>
                        </div>
                        <div className='row flex justify-between'>
                          <h4>Delivery type</h4>
                          <p>{item.deliveryType}</p>
                        </div>
                        <div className='row flex justify-between'>
                          <h4>Status</h4>
                          <span className={`chip ${item.booking_status.code}`}>
                            {item.booking_status.description}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <table className='desktop-table'>
                    <thead>
                      <tr>
                        <th>S/N</th>
                        <th>Date/Time</th>
                        <th>Amount</th>
                        <th>Shipment Type</th>
                        <th>Delivery Type</th>
                        <th>Status</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {bookings.data.map((item: any, i: number) => (
                        <tr key={item.id}>
                          <td>{i + 1}</td>
                          <td className='count'>
                            {new Date(item.createdAt).toLocaleString()}
                          </td>
                          <td className='count'>
                            NGN {item.charge.toLocaleString()}
                          </td>
                          <td className='count'>{item.shipment_type}</td>
                          <td className='count'>{item.deliveryType}</td>
                          <td>
                            <span
                              className={`chip ${item.booking_status.code}`}
                            >
                              {item.booking_status.description}
                            </span>
                          </td>
                          <td>
                            <span
                              onClick={() =>
                                navigate(`/developer/booking/${item._id}`)
                              }
                              className='view'
                            >
                              view
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              ) : (
                <div className='empty flex justify-center'>
                  <div className='empty-cover'>
                    <Icon icon='majesticons:book-line' />
                    <h1>No Recent Bookings To Show</h1>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <div className='home_tables'>
        <div className='left'>
          <div className='left_top'>
            <h2>Recent Transactions</h2>
          </div>
          {transactionsLoading ? (
            <FormLoader position='center' />
          ) : (
            <>
              {transactions.transactions.length > 0 ? (
                <>
                  <div className='mobile-table'>
                    {transactions.transactions.map((item: any, i: number) => (
                      <div className='card' key={item._id}>
                        <div className='flex justify-between row'>
                          <div className='count flex items-center justify-center'>
                            {i + 1}
                          </div>
                          <p> {new Date(item.created_at).toLocaleString()}</p>
                        </div>
                        <div className='row flex justify-between'>
                          <h4>Amount</h4>
                          <p>₦{item.amount.toLocaleString()}</p>
                        </div>
                        <div className='row flex justify-between'>
                          <h4>Type</h4>
                          <p className={item.type}>{item.type}</p>
                        </div>
                        <div className='row flex justify-between'>
                          <h4>Desc</h4>
                          <p>{item.usage}</p>
                        </div>
                        <div className='row flex justify-between'>
                          <h4>Reference</h4>
                          <p>
                            {item.reference.length > 17
                              ? `${item.reference.slice(0, 17)}...`
                              : item.reference}
                          </p>
                        </div>

                        <div className='row flex justify-between'>
                          <h4>Status</h4>
                          <p className={item.status}>{item.status}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <table className='desktop-table'>
                    <thead>
                      <tr>
                        <th>S/N</th>
                        <th>Date/Time</th>
                        <th>Amount</th>
                        <th>Type</th>
                        <th>Use</th>
                        <th>Reference</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.transactions.map((item: any, i: number) => (
                        <tr key={item._id}>
                          <td>{i + 1}</td>
                          <td className='count'>
                            {new Date(item.created_at).toLocaleString()}
                          </td>
                          <td className='count'>
                            NGN {item.amount.toLocaleString()}
                          </td>
                          <td className={item.type}>{item.type}</td>
                          <td>{item.usage}</td>
                          <td>{item.reference}</td>
                          <td className={item.status}>{item.status}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              ) : (
                <div className='empty flex justify-center'>
                  <div className='empty-cover'>
                    <Icon icon='la:clipboard-list' />
                    <h1>No Recent Transaction To Show</h1>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default Home
